import { useState, useEffect, useRef } from 'react';
import type { VersionPlatform } from '@patch-manager/shared';
import { usePlatformDetection } from '../hooks/usePlatformDetection';
import { findMatchingPlatform, getPlatformLabel } from '../utils/platformDetection';

interface SmartDownloadButtonProps {
  platforms: VersionPlatform[];
  versionNumber?: string;
}

/**
 * Smart download button that auto-detects user's platform and shows appropriate download options
 * - Single platform: Simple download button
 * - Multiple platforms with match: Primary button for detected OS + dropdown for others
 * - Multiple platforms without match: Dropdown to choose platform
 * - No download URLs: Shows platform info only (no download button)
 */
export function SmartDownloadButton({ platforms }: SmartDownloadButtonProps) {
  const detectedPlatform = usePlatformDetection();
  const [selectedPlatform, setSelectedPlatform] = useState<VersionPlatform | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Auto-select platform when detection completes
  useEffect(() => {
    if (detectedPlatform && platforms.length > 0 && !selectedPlatform) {
      const match = findMatchingPlatform(platforms, detectedPlatform);
      if (match) {
        setSelectedPlatform(match);
      } else if (platforms.length === 1) {
        // If only one platform and no match, select it anyway
        setSelectedPlatform(platforms[0]);
      }
    }
  }, [detectedPlatform, platforms, selectedPlatform]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // Filter platforms that have download URLs
  const downloadablePlatforms = platforms.filter((p) => p.download_url);

  // If no platforms have downloads, show a disabled button
  if (downloadablePlatforms.length === 0) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-700/30 text-gray-500 border border-gray-700/50 cursor-not-allowed">
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          />
        </svg>
        <span>No Downloads Available</span>
      </div>
    );
  }

  // Single platform case: Simple download button
  if (downloadablePlatforms.length === 1) {
    const platform = downloadablePlatforms[0];
    return (
      <a
        href={platform.download_url!}
        className="btn-primary inline-flex items-center gap-2"
        download
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        <span>Download</span>
        <span className="text-xs opacity-75">
          ({getPlatformLabel(platform.os_platform, platform.architecture)})
        </span>
      </a>
    );
  }

  // Multiple platforms case: Primary button + dropdown
  const primaryPlatform = selectedPlatform || downloadablePlatforms[0];
  const otherPlatforms = downloadablePlatforms.filter((p) => p.id !== primaryPlatform.id);

  return (
    <div className="relative inline-flex" ref={dropdownRef}>
      {/* Primary download button */}
      <a
        href={primaryPlatform.download_url!}
        className="btn-primary inline-flex items-center gap-2 rounded-r-none"
        download
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        <span>
          Download
          {detectedPlatform && selectedPlatform?.id === primaryPlatform.id && (
            <span className="text-xs opacity-75 ml-1">(for your OS)</span>
          )}
        </span>
      </a>

      {/* Dropdown toggle button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="btn-primary rounded-l-none border-l border-opacity-30 border-white px-2"
        title="Other platforms"
      >
        <svg
          className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {showDropdown && otherPlatforms.length > 0 && (
        <div className="absolute top-full mt-2 right-0 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="p-2">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">
              Other Platforms
            </div>
            {otherPlatforms.map((platform) => (
              <a
                key={platform.id}
                href={platform.download_url!}
                download
                onClick={() => {
                  setSelectedPlatform(platform);
                  setShowDropdown(false);
                }}
                className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-sm transition-colors"
              >
                <svg
                  className="w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                <span className="flex-1">
                  {getPlatformLabel(platform.os_platform, platform.architecture)}
                </span>
                {platform.file_size_bytes && (
                  <span className="text-xs text-gray-500">
                    {(platform.file_size_bytes / 1024 / 1024).toFixed(1)} MB
                  </span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
